import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Landmark, Wallet, Receipt, AlertTriangle, CheckCircle2, ExternalLink,
  TrendingUp, Clock, ArrowRight, ShieldCheck,
} from "lucide-react";

// NOTE: this URL is the sandbox claim link produced when the Stripe account was provisioned.
// Replace by polling an "/admin/payments/onboarding-url" if you ever rotate accounts.
const CLAIM_URL = "https://dashboard.stripe.com/onboard_sandbox/YWNjdF8xVUxZakRDV1ozb0swcDM2LDE3OTE0MjU3MDEv100DAkez3Fr";

export default function AdminPayments() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/admin/payments/overview")
      .then(({ data }) => setData(data))
      .catch((e) =>
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load")
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-400">
        Admin only.
      </div>
    );
  }

  const isSandbox = data?.account?.is_sandbox !== false;
  const chargesOn = data?.account?.charges_enabled;
  const payoutsOn = data?.account?.payouts_enabled;

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)]">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
          <div className="flex items-start gap-3">
            <Landmark className="text-[#FBBF24]" size={32} />
            <div>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter">
                Bank &amp; Payments
              </h1>
              <p className="text-zinc-500 mt-2 max-w-2xl">
                Customer card details go directly to Stripe (PCI-DSS Level 1). Every token purchase
                lands in your Stripe balance, and Stripe deposits that balance into the bank
                account you connect below.
              </p>
            </div>
          </div>
          <Link to="/admin" className="btn-outline text-xs">← Back to admin</Link>
        </div>

        {/* Account status banner */}
        {loading ? (
          <div className="card-surface p-6 mb-8 text-zinc-500 text-sm">Loading Stripe account…</div>
        ) : isSandbox ? (
          <div
            data-testid="stripe-claim-banner"
            className="p-6 mb-8 rounded-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(251,191,36,0.12), rgba(251,191,36,0.03))",
              border: "1.5px solid rgba(251,191,36,0.4)",
            }}
          >
            <div className="flex items-start gap-4 flex-wrap">
              <AlertTriangle className="text-[#FBBF24] shrink-0" size={28} />
              <div className="flex-1 min-w-[280px]">
                <div className="font-display font-bold text-xl mb-1">
                  You're in test mode — connect your bank to start accepting real money
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed max-w-2xl">
                  Your Stripe account is live-wired to the site but still in sandbox. Finish Stripe
                  onboarding (business info, government ID, bank account) and payouts will begin.
                  Stripe deposits every 2 business days by default.
                </p>
                <ul className="text-xs text-zinc-500 mt-3 space-y-1">
                  <li>• 1-click secure identity + bank onboarding on Stripe</li>
                  <li>• 2.9% + $0.30 per transaction, no monthly fees</li>
                  <li>• Your bank credentials never touch this app</li>
                </ul>
              </div>
              <a
                href={CLAIM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="claim-stripe-button"
                className="btn-bid text-sm !px-6 flex items-center gap-2 self-start"
              >
                <ShieldCheck size={14} strokeWidth={3} />
                Claim Stripe account
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        ) : (
          <div
            data-testid="stripe-live-banner"
            className="p-6 mb-8 rounded-2xl"
            style={{
              background: "linear-gradient(135deg, rgba(34,197,94,0.12), rgba(34,197,94,0.03))",
              border: "1.5px solid rgba(34,197,94,0.4)",
            }}
          >
            <div className="flex items-start gap-4 flex-wrap">
              <CheckCircle2 className="text-[#22C55E] shrink-0" size={28} />
              <div className="flex-1 min-w-[280px]">
                <div className="font-display font-bold text-xl mb-1">
                  Stripe is live — payouts enabled
                </div>
                <p className="text-zinc-400 text-sm">
                  Charges: {chargesOn ? "enabled ✓" : "pending"} · Payouts:{" "}
                  {payoutsOn ? "enabled ✓" : "pending"}
                </p>
              </div>
              <a
                href={data?.stripe_dashboard_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline text-sm flex items-center gap-2"
                data-testid="open-stripe-dashboard"
              >
                Open Stripe Dashboard <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}

        {/* Money tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Tile
            icon={<Wallet />}
            label="Available in Stripe"
            value={`$${((data?.balance?.available || 0) / 100).toFixed(2)}`}
            sub="Ready for payout"
            testId="tile-stripe-available"
          />
          <Tile
            icon={<Clock />}
            label="Pending in Stripe"
            value={`$${((data?.balance?.pending || 0) / 100).toFixed(2)}`}
            sub="Still settling"
            testId="tile-stripe-pending"
          />
          <Tile
            icon={<TrendingUp />}
            label="Gross revenue"
            value={`$${(data?.db_totals?.gross_revenue_usd ?? 0).toFixed(2)}`}
            sub={`${data?.db_totals?.total_paid_count ?? 0} paid token purchases`}
            testId="tile-gross-revenue"
          />
          <Tile
            icon={<Receipt />}
            label="Last 10 payouts"
            value={(data?.payouts?.length ?? 0).toString()}
            sub={isSandbox ? "Connect a bank to begin" : "Scheduled by Stripe"}
            testId="tile-payouts-count"
          />
        </div>

        {/* Payouts list */}
        <section className="card-surface p-6 mb-6">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
            <ArrowRight size={16} /> Deposits to your bank
          </h2>
          {!data?.payouts?.length ? (
            <p className="text-zinc-500 text-sm">
              No payouts yet.{" "}
              {isSandbox
                ? "Once you claim your Stripe account and your first real sale clears, Stripe will auto-deposit here."
                : "Payouts will appear here once Stripe processes your first batch."}
            </p>
          ) : (
            <ul className="divide-y divide-white/5">
              {data.payouts.map((p) => (
                <li key={p.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">
                      ${(p.amount / 100).toFixed(2)}{" "}
                      <span className="text-xs text-zinc-500">{p.currency?.toUpperCase()}</span>
                    </div>
                    <div className="text-xs text-zinc-500 font-mono-nums">
                      {new Date((p.arrival_date || 0) * 1000).toLocaleDateString()} · {p.method}
                    </div>
                  </div>
                  <span className={`chip ${p.status === "paid" ? "chip-live" : "chip-closed"}`}>
                    {p.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Recent token purchases */}
        <section className="card-surface p-6">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2">
            <Receipt size={16} /> Recent token purchases
          </h2>
          {!data?.recent_payments?.length ? (
            <p className="text-zinc-500 text-sm">
              No paid purchases yet. Share your site — the first customer who buys a pack lands
              here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]" data-testid="recent-payments-table">
                <thead className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <tr>
                    <th className="py-3 pr-4">Date</th>
                    <th className="py-3 pr-4">Customer</th>
                    <th className="py-3 pr-4">Pack</th>
                    <th className="py-3 pr-4">Amount</th>
                    <th className="py-3 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_payments.map((t) => (
                    <tr key={t.session_id} className="border-t border-white/5">
                      <td className="py-3 pr-4 font-mono-nums text-xs text-zinc-400">
                        {new Date(t.created_at).toLocaleString()}
                      </td>
                      <td className="py-3 pr-4">
                        <div>{t.user_name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono-nums">
                          {t.user_email}
                        </div>
                      </td>
                      <td className="py-3 pr-4 capitalize">{t.lookup_key}</td>
                      <td className="py-3 pr-4 font-mono-nums font-bold">
                        ${(t.amount / 100).toFixed(2)}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="chip chip-live">{t.payment_status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {data?.stripe_errors?.length > 0 && (
          <div className="mt-6 text-xs text-zinc-500 font-mono-nums">
            Stripe API notes:
            <ul className="mt-1 list-disc pl-5">
              {data.stripe_errors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ icon, label, value, sub, testId }) {
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
        <span className="text-[#FBBF24]">{icon}</span> {label}
      </div>
      <div className="font-mono-nums font-bold text-2xl">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>
    </div>
  );
}
